import { createHighlighter } from 'shiki';
import { useState, useEffect, useMemo } from 'react';

// Global singleton — WASM loads once
let highlighterInstance = null;
const highlighterPromise = createHighlighter({
  themes: ['dark-plus'],
  langs: ['csharp'],
}).then(h => {
  highlighterInstance = h;
  return h;
});

/**
 * Strip Shiki's <pre><code> wrapper to get raw highlighted spans.
 */
function stripPreWrapper(html) {
  if (!html) return '';
  return html
    .replace(/^<pre[^>]*><code[^>]*>/, '')
    .replace(/<\/code><\/pre>$/, '');
}

/**
 * Hook: returns highlighted HTML for given code string.
 * Returns empty string while Shiki is loading.
 */
export function useShikiHighlight(code, lang = 'csharp') {
  const [ready, setReady] = useState(!!highlighterInstance);

  useEffect(() => {
    if (!ready) {
      highlighterPromise.then(() => setReady(true));
    }
  }, [ready]);

  const html = useMemo(() => {
    if (!ready || !highlighterInstance || !code) return '';
    const raw = highlighterInstance.codeToHtml(code, {
      lang,
      theme: 'dark-plus',
    });
    return stripPreWrapper(raw);
  }, [code, lang, ready]);

  return html;
}

/**
 * Synchronous highlight — for Remotion frame rendering.
 * Requires highlighter already initialized.
 */
export function highlightSync(code, lang = 'csharp') {
  if (!highlighterInstance || !code) return '';
  const raw = highlighterInstance.codeToHtml(code, { lang, theme: 'dark-plus' });
  return stripPreWrapper(raw);
}

export { highlighterPromise };
